import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { 
  MessageSquare, 
  ClipboardList, 
  Languages, 
  Code, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  DollarSign, 
  Shield, 
  Users, 
  Calendar, 
  Briefcase, 
  Lightbulb,
  Brain,
  Network,
  Bot,
  Workflow,
  BarChart3,
  Target,
  Layers,
  Cpu,
  GitBranch,
  Timer,
  Activity,
  Award,
  Globe,
  Lock,
  Sparkles,
  Rocket,
  CircuitBoard,
  Database,
  Settings,
  ChevronRight,
  Play,
  Star,
  Building2
} from 'lucide-react';

const HomePage = () => {
  return (
    <>
      <SEO 
        title="AI Integration & Intelligent Automation Solutions | Abacies Logiciels"
        description="Leading provider of AI Orchestration, Intelligent Chatbots, Rule Engines, and NLP Solutions. Transform your business with advanced AI integration, conversational AI, and decision automation systems."
        keywords="AI integration, intelligent chatbots, rule engines, NLP, conversational AI, AI orchestration, decision engines, temporal analysis, machine learning, business automation"
      />

      {/* Two-Column Hero Section with AI Focus - Minimal Top Spacing */}
      <section className="pt-5 pb-6 md:pt-18 md:pb-10 lg:pt-12 lg:pb-8 bg-gradient-to-br from-pearl-white to-alabaster relative overflow-hidden">
        {/* Matrix-style Falling Hex Codes Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="matrix-rain-container">
            {/* Column 1 */}
            <div className="matrix-column" style={{left: '5%', animationDelay: '0s'}}>
              <span className="hex-code">#E63946</span>
              <span className="hex-code">#D62828</span>
              <span className="hex-code">#F28482</span>
              <span className="hex-code">#FFCAD4</span>
              <span className="hex-code">#495057</span>
            </div>
            {/* Column 2 */}
            <div className="matrix-column" style={{left: '15%', animationDelay: '0.5s'}}>
              <span className="hex-code">#F8F9FA</span>
              <span className="hex-code">#E9ECEF</span>
              <span className="hex-code">#ADB5BD</span>
              <span className="hex-code">#212529</span>
              <span className="hex-code">#E63946</span>
            </div>
            {/* Column 2.5 */}
            <div className="matrix-column" style={{left: '20%', animationDelay: '0.75s'}}>
              <span className="hex-code">#F8F9FA</span>
              <span className="hex-code">#E9ECEF</span>
              <span className="hex-code">#ADB5BD</span>
              <span className="hex-code">#212529</span>
              <span className="hex-code">#E63946</span>
            </div>
            {/* Column 3 */}
            <div className="matrix-column" style={{left: '25%', animationDelay: '1s'}}>
              <span className="hex-code">#6C757D</span>
              <span className="hex-code">#F0F2F5</span>
              <span className="hex-code">#D62828</span>
              <span className="hex-code">#F28482</span>
              <span className="hex-code">#495057</span>
            </div>
            {/* Column 4 */}
            <div className="matrix-column" style={{left: '35%', animationDelay: '1.5s'}}>
              <span className="hex-code">#FFCAD4</span>
              <span className="hex-code">#E63946</span>
              <span className="hex-code">#ADB5BD</span>
              <span className="hex-code">#F8F9FA</span>
              <span className="hex-code">#212529</span>
            </div>
            {/* Column 4.5 */}
            <div className="matrix-column" style={{left: '40%', animationDelay: '1.7s'}}>
              <span className="hex-code">#FFCAD4</span>
              <span className="hex-code">#E63946</span>
              <span className="hex-code">#ADB5BD</span>
              <span className="hex-code">#F8F9FA</span>
              <span className="hex-code">#212529</span>
            </div>
            {/* Column 5 */}
            <div className="matrix-column" style={{left: '45%', animationDelay: '2s'}}>
              <span className="hex-code">#F28482</span>
              <span className="hex-code">#6C757D</span>
              <span className="hex-code">#E9ECEF</span>
              <span className="hex-code">#D62828</span>
              <span className="hex-code">#F0F2F5</span>
            </div>
            {/* Column 5.5 */}
            <div className="matrix-column" style={{left: '50%', animationDelay: '2.2s'}}>
              <span className="hex-code">#F28482</span>
              <span className="hex-code">#6C757D</span>
              <span className="hex-code">#E9ECEF</span>
              <span className="hex-code">#D62828</span>
              <span className="hex-code">#F0F2F5</span>
            </div>
            {/* Column 6 */}
            <div className="matrix-column" style={{left: '55%', animationDelay: '2.5s'}}>
              <span className="hex-code">#495057</span>
              <span className="hex-code">#FFCAD4</span>
              <span className="hex-code">#E63946</span>
              <span className="hex-code">#F8F9FA</span>
              <span className="hex-code">#ADB5BD</span>
            </div>
            {/* Column 7 */}
            <div className="matrix-column" style={{left: '65%', animationDelay: '3s'}}>
              <span className="hex-code">#212529</span>
              <span className="hex-code">#F28482</span>
              <span className="hex-code">#E9ECEF</span>
              <span className="hex-code">#D62828</span>
              <span className="hex-code">#6C757D</span>
            </div>
            {/* Column 8 */}
            <div className="matrix-column" style={{left: '75%', animationDelay: '3.5s'}}>
              <span className="hex-code">#F0F2F5</span>
              <span className="hex-code">#E63946</span>
              <span className="hex-code">#495057</span>
              <span className="hex-code">#FFCAD4</span>
              <span className="hex-code">#F8F9FA</span>
            </div>
            {/* Column 9 */}
            <div className="matrix-column" style={{left: '85%', animationDelay: '4s'}}>
              <span className="hex-code">#ADB5BD</span>
              <span className="hex-code">#D62828</span>
              <span className="hex-code">#212529</span>
              <span className="hex-code">#F28482</span>
              <span className="hex-code">#E9ECEF</span>
            </div>
            {/* Column 10 */}
            <div className="matrix-column" style={{left: '95%', animationDelay: '4.5s'}}>
              <span className="hex-code">#6C757D</span>
              <span className="hex-code">#E63946</span>
              <span className="hex-code">#F0F2F5</span>
              <span className="hex-code">#495057</span>
              <span className="hex-code">#FFCAD4</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-4 lg:gap-8 items-center">
            {/* Left Column - Content */}
            <div className="order-2 lg:order-1 animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-red/10 rounded-full mb-4">
                <span className="w-2 h-2 bg-primary-red rounded-full animate-pulse"></span>
                <span className="text-sm font-semibold text-primary-red">AI-Powered Enterprise Solutions</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-charcoal">
                Transform Your Business with 
                <span className="block text-gradient mt-2 pb-3">Intelligent AI Integration</span>
              </h1>
              
              <p className="text-lg md:text-xl text-titanium mb-6 leading-relaxed">
                Harness the power of <strong>AI Orchestration</strong>, <strong>Intelligent Chatbots</strong>, and <strong>Rule Engines</strong> to automate decisions, enhance customer experiences, and scale your operations exponentially.
              </p>

              {/* Key AI Features List */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary-red flex-shrink-0" />
                  <span className="text-charcoal text-base font-medium">NLP & Conversational AI</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary-red flex-shrink-0" />
                  <span className="text-charcoal text-base font-medium">Decision & Rule Engines</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary-red flex-shrink-0" />
                  <span className="text-charcoal text-base font-medium">Intelligent Routing Engine</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary-red flex-shrink-0" />
                  <span className="text-charcoal text-base font-medium">Temporal Analysis Engine</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="bg-primary-red text-white px-8 py-3.5 rounded-lg text-base font-semibold hover:bg-deep-garnet transition-all duration-300 shadow-lg text-center inline-flex items-center justify-center gap-2">
                  <span>Start AI Integration</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/ai-hub" className="border-2 border-primary-red text-primary-red px-8 py-3.5 rounded-lg text-base font-semibold hover:bg-primary-red hover:text-white transition-all duration-300 text-center inline-flex items-center justify-center gap-2">
                  <span>Explore AI Solutions</span>
                  <Lightbulb className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Right Column - Enhanced Visual Stats & Features with Animations */}
            <div className="order-1 lg:order-2 relative animate-fade-in">
              {/* Floating Experience Tags - Higher z-index */}
              <div className="absolute right-2 top-5 hidden lg:block z-20">
                <div className="bg-white backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold text-primary-red shadow-md animate-float border border-primary-red/20">
                  🚀 10+ Years Experience
                </div>
              </div>
              
              {/* <div className="absolute -right-0 bottom-16 hidden lg:block z-20">
                <div className="bg-white backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold text-primary-red shadow-md animate-float-delayed border border-primary-red/20">
                  ⚡ Real-time Processing
                </div>
              </div> */}

              <div className="absolute right-2 bottom-10 hidden lg:block z-20">
                <div className="bg-white backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold text-primary-red shadow-md animate-float-delayed border border-primary-red/20">
                  ⚡ Real-time Processing
                </div> 
              </div>
              
              {/* Main Card without background animation */}
              <div className="relative bg-white rounded-xl shadow-xl border border-platinum p-6 overflow-hidden z-10">
                
                {/* Rotating Text Header */}
                <div className="relative mb-4">
                  <h3 className="text-lg font-bold text-charcoal">Core AI Capabilities</h3>
                  <div className="text-xs text-primary-red font-semibold mt-0.5">
                    <span className="inline-block animate-typing">
                      Powering Next-Gen Intelligence
                    </span>
                    <span className="animate-blink">|</span>
                  </div>
                </div>

                {/* AI Capabilities with Progress Bars and Animations */}
                <div className="space-y-3 mb-6 relative">
                  <div className="flex items-center justify-between p-2.5 bg-gradient-to-r from-blush-light/20 to-transparent rounded-lg hover:from-blush-light/40 transition-all transform hover:scale-105 hover:shadow-md">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary-red/10 rounded-lg flex items-center justify-center animate-bounce-slow">
                        <Bot className="w-5 h-5 text-primary-red animate-pulse" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-charcoal text-sm">Intelligent Chatbots</div>
                        <div className="text-xs text-titanium">24/7 Automated Support</div>
                        {/* Animated Progress Bar */}
                        <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                          <div className="bg-gradient-to-r from-primary-red to-coral-mist h-1 rounded-full animate-progress-98" style={{width: '98%'}}></div>
                        </div>
                      </div>
                    </div>
                    <span className="text-primary-red font-bold text-sm animate-counter">98%</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 bg-gradient-to-r from-blush-light/20 to-transparent rounded-lg hover:from-blush-light/40 transition-all transform hover:scale-105 hover:shadow-md" style={{animationDelay: '0.1s'}}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary-red/10 rounded-lg flex items-center justify-center animate-spin-slow">
                        <Workflow className="w-5 h-5 text-primary-red" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-charcoal text-sm">Rule Engines</div>
                        <div className="text-xs text-titanium">Lightning-Fast Decisions</div>
                        {/* Speed Indicator */}
                        <div className="flex items-center gap-1 mt-1">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping"></div>
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
                        </div>
                      </div>
                    </div>
                    <span className="text-primary-red font-bold text-sm animate-flash">10ms</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 bg-gradient-to-r from-blush-light/20 to-transparent rounded-lg hover:from-blush-light/40 transition-all transform hover:scale-105 hover:shadow-md" style={{animationDelay: '0.2s'}}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary-red/10 rounded-lg flex items-center justify-center animate-pulse-slow">
                        <Brain className="w-5 h-5 text-primary-red" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-charcoal text-sm">NLP Processing</div>
                        <div className="text-xs text-titanium">Language Understanding</div>
                        {/* Animated Progress Bar */}
                        <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                          <div className="bg-gradient-to-r from-primary-red to-coral-mist h-1 rounded-full animate-progress-95" style={{width: '95%'}}></div>
                        </div>
                      </div>
                    </div>
                    <span className="text-primary-red font-bold text-sm animate-counter-delayed">95%</span>
                  </div>
                </div>

                {/* Animated Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="text-center p-3 bg-gradient-to-br from-alabaster to-white rounded-lg transform hover:scale-105 transition-all hover:shadow-md group">
                    <div className="text-2xl font-bold text-primary-red mb-0.5 animate-count-up">500M+</div>
                    <div className="text-xs text-titanium group-hover:text-primary-red transition-colors">AI Interactions</div>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-alabaster to-white rounded-lg transform hover:scale-105 transition-all hover:shadow-md group" style={{animationDelay: '0.1s'}}>
                    <div className="text-2xl font-bold text-primary-red mb-0.5 animate-count-up-delayed">99.9%</div>
                    <div className="text-xs text-titanium group-hover:text-primary-red transition-colors">Uptime SLA</div>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-alabaster to-white rounded-lg transform hover:scale-105 transition-all hover:shadow-md group" style={{animationDelay: '0.2s'}}>
                    <div className="text-2xl font-bold text-primary-red mb-0.5 animate-count-up-delayed-2">50ms</div>
                    <div className="text-xs text-titanium group-hover:text-primary-red transition-colors">Response Time</div>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-alabaster to-white rounded-lg transform hover:scale-105 transition-all hover:shadow-md group" style={{animationDelay: '0.3s'}}>
                    <div className="text-2xl font-bold text-primary-red mb-0.5 animate-glow">24/7</div>
                    <div className="text-xs text-titanium group-hover:text-primary-red transition-colors">AI Support</div>
                  </div>
                </div>

                {/* Live Activity Indicator */}
                <div className="my-4 p-2 bg-gradient-to-r from-green-50 to-transparent rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-semibold text-green-700">Live System Status</span>
                    </div>
                    <div className="text-xs text-titanium animate-fade-in-out">
                      <span className="inline-block">Processing </span>
                      <span className="inline-block animate-counter-live">1,247</span>
                      <span className="inline-block"> req/sec</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Experience Badges - Outside card with higher z-index */}
              <div className="mt-3 flex flex-wrap gap-2 justify-center relative z-20">
                <span className="px-2 py-1 bg-white backdrop-blur-sm rounded-full text-xs font-semibold text-graphite border border-platinum animate-slide-up shadow-sm" style={{animationDelay: '0.4s'}}>
                  🏆 Industry Leader
                </span>
                <span className="px-2 py-1 bg-white backdrop-blur-sm rounded-full text-xs font-semibold text-graphite border border-platinum animate-slide-up shadow-sm" style={{animationDelay: '0.5s'}}>
                  🔒 Enterprise Security
                </span>
                <span className="px-2 py-1 bg-white backdrop-blur-sm rounded-full text-xs font-semibold text-graphite border border-platinum animate-slide-up shadow-sm" style={{animationDelay: '0.6s'}}>
                  🌍 Global Scale
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Services Section - Compact Modern Design */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Compact Header with Stats */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <p className="text-primary-red font-semibold text-sm uppercase tracking-wider mb-2">AI Integration Services</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-charcoal">
                Advanced AI Solutions for <span className="text-primary-red">Enterprise Excellence</span>
              </h2>
              <p className="text-titanium mt-3">
                Unlock the full potential of AI with enterprise-grade services that deliver measurable business outcomes.
              </p>
            </div>
            
            {/* Inline Stats */}
            <div className="flex flex-wrap gap-6 lg:gap-8">
              {[
                { value: '10x', label: 'Faster' },
                { value: '99.9%', label: 'Accuracy' },
                { value: '500+', label: 'Projects' }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold text-primary-red">{stat.value}</div>
                  <div className="text-xs text-titanium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Compact Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                title: 'AI-Powered Chatbots',
                description: 'Intelligent conversational AI with context understanding and 24/7 personalized experiences.',
                icon: Bot,
                features: ['NLU', 'Multi-language', 'Sentiment Analysis'],
                stat: '98% Resolution'
              },
              {
                title: 'Business Rule Engines',
                description: 'Automate complex decisions with configurable rules that adapt in real-time.',
                icon: Workflow,
                features: ['Dynamic Rules', 'Real-time', 'Compliance'],
                stat: '<10ms Response'
              },
              {
                title: 'NLP & Language AI',
                description: 'Extract insights from unstructured data with advanced language processing.',
                icon: Languages,
                features: ['Text Analytics', 'Entity Recognition', 'Translation'],
                stat: '50+ Languages'
              },
              {
                title: 'AI Orchestration',
                description: 'Centralize multiple AI services with comprehensive orchestration platform.',
                icon: Network,
                features: ['Service Coordination', 'Workflow', 'Monitoring'],
                stat: '99.99% Uptime'
              },
              {
                title: 'Intelligent Routing',
                description: 'ML-powered routing that learns and adapts to your traffic patterns.',
                icon: GitBranch,
                features: ['Load Balancing', 'Predictive', 'Failover'],
                stat: '10M+ Routes/Day'
              },
              {
                title: 'Temporal Analysis',
                description: 'Time-series analysis and forecasting with predictive modeling.',
                icon: Timer,
                features: ['Forecasting', 'Anomaly Detection', 'Trends'],
                stat: '95% Accuracy'
              }
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="group relative bg-pearl-white hover:bg-white rounded-xl p-5 border border-transparent hover:border-platinum hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-12 h-12 bg-primary-red/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary-red group-hover:scale-105 transition-all duration-300">
                      <IconComponent className="w-6 h-6 text-primary-red group-hover:text-white transition-colors duration-300" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="font-bold text-charcoal group-hover:text-primary-red transition-colors truncate">{service.title}</h3>
                        <span className="text-xs font-semibold text-primary-red bg-primary-red/10 px-2 py-0.5 rounded-full whitespace-nowrap">{service.stat}</span>
                      </div>
                      <p className="text-sm text-titanium mb-3 line-clamp-2">{service.description}</p>
                      
                      {/* Features Tags */}
                      <div className="flex flex-wrap gap-1.5">
                        {service.features.map((feature, idx) => (
                          <span key={idx} className="text-xs text-graphite bg-white px-2 py-0.5 rounded border border-platinum">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Compact CTA */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/services/ai-machine-learning" className="text-primary-red font-semibold hover:underline inline-flex items-center gap-1">
              View All AI Services <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="hidden sm:block text-platinum">|</span>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 text-sm px-5 py-2.5">
              <Play className="w-4 h-4" />
              Request Demo
            </Link>
          </div>
        </div>
      </section>

      {/* How We Work - Corporate Process Section */}
      <section className="py-10 md:py-18 bg-alabaster relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
            <div className="max-w-2xl">
              <p className="text-primary-red font-semibold text-sm uppercase tracking-wider mb-3">Our Process</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-charcoal leading-tight">
                How We Deliver <span className="text-primary-red">Excellence</span>
              </h2>
              <p className="text-titanium text-lg mt-4">
                A structured, transparent approach that ensures successful outcomes and lasting partnerships.
              </p>
            </div>
            <div className="mt-6 lg:mt-0">
              <Link to="/contact" className="inline-flex items-center gap-2 text-primary-red font-semibold hover:gap-3 transition-all duration-300">
                Start a Project
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Process Steps - Desktop */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-0 relative">
            {/* Horizontal Line */}
            <div className="absolute top-8 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-platinum to-transparent"></div>
            
            {[
              { 
                step: '01', 
                title: 'Discovery', 
                desc: 'We analyze your business objectives, existing infrastructure, and identify opportunities for AI-driven transformation.',
                highlights: ['Stakeholder interviews', 'Technical assessment', 'Opportunity mapping']
              },
              { 
                step: '02', 
                title: 'Strategy', 
                desc: 'Our experts design a comprehensive roadmap with clear milestones, resource allocation, and success metrics.',
                highlights: ['Solution architecture', 'Project timeline', 'Risk mitigation']
              },
              { 
                step: '03', 
                title: 'Implementation', 
                desc: 'Agile development with iterative delivery, ensuring alignment with your goals at every stage.',
                highlights: ['Sprint-based delivery', 'Quality assurance', 'Continuous integration']
              },
              { 
                step: '04', 
                title: 'Optimization', 
                desc: 'Ongoing monitoring, performance tuning, and strategic enhancements to maximize your ROI.',
                highlights: ['Performance analytics', 'Iterative improvements', '24/7 support']
              }
            ].map((phase, index) => (
              <div key={index} className="relative px-6 group">
                {/* Step Number Circle */}
                <div className="relative z-10 mb-8">
                  <div className="w-16 h-16 bg-white border-2 border-platinum rounded-full flex items-center justify-center mx-auto shadow-sm transition-all duration-300 group-hover:border-primary-red group-hover:shadow-lg">
                    <span className="text-xl font-bold text-charcoal group-hover:text-primary-red transition-colors duration-300">{phase.step}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="text-center">
                  <h3 className="text-xl font-bold text-charcoal mb-3 group-hover:text-primary-red transition-colors duration-300">{phase.title}</h3>
                  <p className="text-titanium text-sm leading-relaxed mb-4">{phase.desc}</p>
                  
                  {/* Highlights */}
                  <ul className="space-y-2">
                    {phase.highlights.map((item, i) => (
                      <li key={i} className="flex items-center justify-center gap-2 text-sm text-graphite">
                        <CheckCircle className="w-4 h-4 text-primary-red flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Process Steps - Mobile/Tablet */}
          <div className="lg:hidden space-y-8">
            {[
              { 
                step: '01', 
                title: 'Discovery', 
                desc: 'We analyze your business objectives, existing infrastructure, and identify opportunities for AI-driven transformation.',
                highlights: ['Stakeholder interviews', 'Technical assessment', 'Opportunity mapping']
              },
              { 
                step: '02', 
                title: 'Strategy', 
                desc: 'Our experts design a comprehensive roadmap with clear milestones, resource allocation, and success metrics.',
                highlights: ['Solution architecture', 'Project timeline', 'Risk mitigation']
              },
              { 
                step: '03', 
                title: 'Implementation', 
                desc: 'Agile development with iterative delivery, ensuring alignment with your goals at every stage.',
                highlights: ['Sprint-based delivery', 'Quality assurance', 'Continuous integration']
              },
              { 
                step: '04', 
                title: 'Optimization', 
                desc: 'Ongoing monitoring, performance tuning, and strategic enhancements to maximize your ROI.',
                highlights: ['Performance analytics', 'Iterative improvements', '24/7 support']
              }
            ].map((phase, index) => (
              <div key={index} className="flex gap-6">
                {/* Left: Step Number & Line */}
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 bg-white border-2 border-primary-red rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-primary-red">{phase.step}</span>
                  </div>
                  {index < 3 && (
                    <div className="w-px h-full bg-gradient-to-b from-primary-red to-platinum mt-4"></div>
                  )}
                </div>

                {/* Right: Content */}
                <div className="flex-1 pb-8">
                  <h3 className="text-xl font-bold text-charcoal mb-2">{phase.title}</h3>
                  <p className="text-titanium text-sm leading-relaxed mb-4">{phase.desc}</p>
                  
                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2">
                    {phase.highlights.map((item, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-alabaster rounded-full text-xs text-graphite">
                        <CheckCircle className="w-3 h-3 text-primary-red" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Stats */}
          <div className="mt-10 p-8 md:p-12 bg-white rounded-2xl shadow-sm border border-platinum">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: '98%', label: 'Project Success Rate' },
                { value: '2-4x', label: 'Average ROI' },
                { value: '<30', label: 'Days to First Delivery' },
                { value: '100+', label: 'Enterprise Clients' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-red mb-1">{stat.value}</div>
                  <div className="text-sm text-titanium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Success Metrics & Client Stories - Modern Design */}
      <section className="py-10 md:py-14 bg-white relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-red/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-coral-mist/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 gap-6">
            <div className="max-w-2xl">
              <p className="text-primary-red font-semibold text-sm uppercase tracking-wider mb-2">Proven Results</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-charcoal leading-tight">
                Delivering <span className="text-primary-red">Real Impact</span> for Industry Leaders
              </h2>
              <p className="text-titanium text-lg mt-3">
                Measurable outcomes that drive business transformation and competitive advantage.
              </p>
            </div>
            
            {/* Quick View Link */}
            <Link to="/contact" className="inline-flex items-center gap-2 text-primary-red font-semibold hover:gap-3 transition-all duration-300 whitespace-nowrap">
              View All Success Stories
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Impact Metrics - Modern Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
            {[
              { 
                icon: Zap, 
                value: '75%', 
                label: 'Faster Response', 
                sublabel: 'Average improvement in processing time',
                gradient: 'from-orange-500 to-primary-red'
              },
              { 
                icon: DollarSign, 
                value: '60%', 
                label: 'Cost Reduction', 
                sublabel: 'Operational savings through automation',
                gradient: 'from-green-500 to-emerald-600'
              },
              { 
                icon: Target, 
                value: '95%', 
                label: 'Accuracy Rate', 
                sublabel: 'AI decision precision and reliability',
                gradient: 'from-blue-500 to-primary-red'
              },
              { 
                icon: TrendingUp, 
                value: '10M+', 
                label: 'AI Decisions', 
                sublabel: 'Processed and executed daily',
                gradient: 'from-purple-500 to-primary-red'
              }
            ].map((metric, index) => {
              const IconComponent = metric.icon;
              return (
                <div key={index} className="group relative">
                  <div className="relative bg-white rounded-2xl p-6 border-2 border-platinum hover:border-primary-red/30 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                    {/* Icon with gradient background */}
                    <div className="mb-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${metric.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    
                    {/* Value */}
                    <div className="text-4xl md:text-5xl font-bold text-charcoal mb-1 group-hover:text-primary-red transition-colors">
                      {metric.value}
                    </div>
                    
                    {/* Label */}
                    <div className="text-base font-semibold text-charcoal mb-2">
                      {metric.label}
                    </div>
                    
                    {/* Sublabel */}
                    <div className="text-xs text-titanium leading-relaxed">
                      {metric.sublabel}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Case Studies Grid - Modern Two-Column Layout */}
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                industry: 'Financial Services',
                company: 'Global Bank Corp',
                icon: Briefcase,
                iconBg: 'bg-blue-500',
                result: '80%',
                resultLabel: 'Faster Fraud Detection',
                description: 'AI-powered fraud detection system with real-time rule engine reduced detection time from hours to minutes, preventing millions in potential losses.',
                metrics: [
                  { icon: Clock, value: '<2min', label: 'Detection Time' },
                  { icon: Shield, value: '$50M+', label: 'Protected' },
                  { icon: CheckCircle, value: '99.8%', label: 'Accuracy' }
                ],
                tags: ['Rule Engine', 'Real-time AI', 'Security']
              },
              {
                industry: 'Healthcare',
                company: 'MediCare Plus',
                icon: Activity,
                iconBg: 'bg-green-500',
                result: '65%',
                resultLabel: 'Better Patient Response',
                description: 'Intelligent chatbots provide 24/7 patient support, triage, and appointment scheduling, significantly improving patient satisfaction scores.',
                metrics: [
                  { icon: Users, value: '500K+', label: 'Patients Served' },
                  { icon: MessageSquare, value: '24/7', label: 'Availability' },
                  { icon: Star, value: '4.8/5', label: 'Satisfaction' }
                ],
                tags: ['Chatbots', 'NLP', 'Patient Care']
              },
              {
                industry: 'E-commerce',
                company: 'ShopGlobal Inc',
                icon: TrendingUp,
                iconBg: 'bg-purple-500',
                result: '45%',
                resultLabel: 'Higher Conversion Rate',
                description: 'NLP-based product recommendations and personalized customer service automation increased conversion rates and customer lifetime value.',
                metrics: [
                  { icon: DollarSign, value: '+$12M', label: 'Revenue' },
                  { icon: Users, value: '2M+', label: 'Customers' },
                  { icon: Zap, value: '3x', label: 'Engagement' }
                ],
                tags: ['NLP', 'Recommendations', 'Automation']
              }
            ].map((story, index) => {
              const IconComponent = story.icon;
              return (
                <div key={index} className="group bg-white rounded-2xl border-2 border-platinum hover:border-primary-red/30 transition-all duration-300 overflow-hidden hover:shadow-2xl">
                  {/* Card Header with Icon */}
                  <div className="bg-gradient-to-br from-charcoal to-graphite p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-14 h-14 ${story.iconBg} rounded-xl flex items-center justify-center shadow-lg`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-bold text-lg">{story.company}</div>
                        <div className="text-white/60 text-sm">{story.industry}</div>
                      </div>
                    </div>
                    
                    {/* Result Highlight */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-primary-red">{story.result}</span>
                      <span className="text-white text-base font-medium">{story.resultLabel}</span>
                    </div>
                  </div>
                  
                  {/* Card Body */}
                  <div className="p-6">
                    <p className="text-titanium leading-relaxed mb-6">{story.description}</p>
                    
                    {/* Key Metrics */}
                    <div className="grid grid-cols-3 gap-3 mb-6 pb-6 border-b border-platinum">
                      {story.metrics.map((metric, i) => {
                        const MetricIcon = metric.icon;
                        return (
                          <div key={i} className="text-center">
                            <MetricIcon className="w-5 h-5 text-primary-red mx-auto mb-1" />
                            <div className="text-sm font-bold text-charcoal">{metric.value}</div>
                            <div className="text-xs text-titanium">{metric.label}</div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Technology Tags */}
                    <div className="flex flex-wrap gap-2">
                      {story.tags.map((tag, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-primary-red/5 text-primary-red rounded-full font-medium border border-primary-red/10">
                          <CheckCircle className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Trust Indicators */}
          <div className="mt-12 pt-12 border-t border-platinum">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Shield, label: 'ISO 27001 Certified', sublabel: 'Enterprise Security' },
                { icon: Award, label: 'SOC 2 Compliant', sublabel: 'Data Protection' },
                { icon: Globe, label: '20+ Countries', sublabel: 'Global Reach' },
                { icon: Users, label: '100+ Enterprises', sublabel: 'Trusted Worldwide' }
              ].map((item, i) => {
                const ItemIcon = item.icon;
                return (
                  <div key={i} className="flex items-center gap-3 p-4 bg-pearl-white rounded-xl border border-platinum hover:border-primary-red/20 transition-all duration-300">
                    <div className="w-10 h-10 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ItemIcon className="w-5 h-5 text-primary-red" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-charcoal">{item.label}</div>
                      <div className="text-xs text-titanium">{item.sublabel}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Strip - Modern Design */}
      <section className="py-10 md:py-12 bg-gradient-to-br from-charcoal via-graphite to-charcoal relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-red rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-coral-mist rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
          
          {/* Grid pattern */}
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-red/20 border border-primary-red/30 rounded-full">
              <Star className="w-4 h-4 text-coral-mist" />
              <span className="text-sm font-semibold text-white">Client Success Stories</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Testimonial Card - Left Side */}
            <div className="relative">
              {/* Quote decoration */}
              <div className="absolute -top-6 -left-4 text-9xl text-primary-red/20 font-serif leading-none select-none">"</div>
              
              <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300">
                {/* Quote content */}
                <div className="mb-6">
                  <p className="text-white text-lg md:text-xl font-medium leading-relaxed">
                    Abacies transformed our customer service with their AI chatbot solution. The implementation was seamless and the results exceeded our expectations. Our response times dropped by 70% within the first month, and customer satisfaction scores improved dramatically.
                  </p>
                </div>

                {/* Rating stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-coral-mist fill-coral-mist" />
                  ))}
                </div>

                {/* Author info */}
                <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-red to-coral-mist rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      JM
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-charcoal rounded-full"></div>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-bold text-lg">James Mitchell</div>
                    <div className="text-white/60 text-sm">Chief Technology Officer</div>
                    <div className="text-coral-mist text-sm font-medium">Global Bank Corp</div>
                  </div>
                  <div className="hidden sm:block">
                    <Briefcase className="w-8 h-8 text-white/20" />
                  </div>
                </div>
              </div>

              {/* Small verification badge */}
              <div className="absolute -bottom-4 right-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-xs font-semibold text-white">Verified Client</span>
              </div>
            </div>

            {/* Stats Grid - Right Side */}
            <div className="grid grid-cols-2 gap-6">
              {[
                { 
                  icon: Users, 
                  value: '100+', 
                  label: 'Enterprise Clients',
                  sublabel: 'Trusted globally',
                  gradient: 'from-blue-500 to-primary-red'
                },
                { 
                  icon: Globe, 
                  value: '20+', 
                  label: 'Countries Served',
                  sublabel: 'Worldwide presence',
                  gradient: 'from-green-500 to-primary-red'
                },
                { 
                  icon: Star, 
                  value: '99%', 
                  label: 'Client Satisfaction',
                  sublabel: 'Average rating',
                  gradient: 'from-yellow-500 to-primary-red'
                },
                { 
                  icon: Award, 
                  value: '50+', 
                  label: 'Industry Awards',
                  sublabel: 'Recognition earned',
                  gradient: 'from-purple-500 to-primary-red'
                }
              ].map((stat, i) => {
                const StatIcon = stat.icon;
                return (
                  <div key={i} className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-coral-mist/30 transition-all duration-300 hover:-translate-y-2">
                    {/* Icon with gradient background */}
                    <div className="mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <StatIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    {/* Value */}
                    <div className="text-3xl md:text-4xl font-bold text-white mb-1 group-hover:text-coral-mist transition-colors">
                      {stat.value}
                    </div>

                    {/* Label */}
                    <div className="text-sm font-semibold text-white mb-1">
                      {stat.label}
                    </div>

                    {/* Sublabel */}
                    <div className="text-xs text-white/60">
                      {stat.sublabel}
                    </div>

                    {/* Hover glow effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-red/0 to-coral-mist/0 group-hover:from-primary-red/5 group-hover:to-coral-mist/5 transition-all duration-300 pointer-events-none"></div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom trust indicators */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="flex items-center gap-2 text-white/60">
                <Shield className="w-5 h-5 text-coral-mist" />
                <span className="text-sm">ISO 27001 Certified</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Award className="w-5 h-5 text-coral-mist" />
                <span className="text-sm">SOC 2 Type II</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <CheckCircle className="w-5 h-5 text-coral-mist" />
                <span className="text-sm">GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Building2 className="w-5 h-5 text-coral-mist" />
                <span className="text-sm">Fortune 500 Trusted</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative floating elements */}
        <div className="absolute top-10 right-10 w-20 h-20 border-2 border-primary-red/20 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-16 h-16 border-2 border-coral-mist/20 rounded-lg rotate-45 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      </section>

      {/* CTA Section - Modern Design */}
      <section className="relative py-12 md:py-18 bg-gradient-to-br from-primary-red via-deep-garnet to-primary-red overflow-hidden">
        {/* Animated background effects */}
        <div className="absolute inset-0 opacity-20">
          {/* Gradient orbs */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-coral-mist rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          
          {/* Grid pattern */}
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Top badge */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 border border-white/30 rounded-full text-white">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-semibold">Transform Your Business Today</span>
              </div>
            </div>

            {/* Main heading */}
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-center text-white leading-tight">
              Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-pearl-white to-white drop-shadow-lg">Revolutionize</span> with AI?
            </h2>
            
            {/* Subheading */}
            <p className="text-lg md:text-xl text-white/90 mb-10 text-center leading-relaxed">
              Join 100+ industry leaders who have transformed their operations with our AI solutions. 
              <span className="text-white font-semibold"> Start your journey in just 30 minutes.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact" className="group relative inline-flex items-center justify-center gap-2 bg-white text-primary-red hover:bg-pearl-white px-8 py-5 rounded-xl text-lg font-semibold transition-all duration-300 shadow-2xl hover:shadow-white/50 hover:scale-105">
                <Rocket className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                Start Your Project
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/ai-hub" className="group inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border-2 border-white/50 hover:bg-white hover:text-primary-red px-8 py-5 rounded-xl text-lg font-semibold text-white transition-all duration-300 hover:scale-105">
                <Sparkles className="w-5 h-5" />
                Explore AI Solutions
              </Link>
            </div>

            {/* Trust indicators grid */}
            <div className="grid md:grid-cols-3 gap-4 mb-12">
              {[
                {
                  icon: Rocket,
                  title: 'Fast Deployment',
                  description: 'Live in 2-4 weeks average'
                },
                {
                  icon: Users,
                  title: 'Dedicated Team',
                  description: '24/7 expert support included'
                },
                {
                  icon: Target,
                  title: 'Proven Results',
                  description: '2-4x ROI in first year'
                }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                    <div className="w-10 h-10 bg-white/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{item.title}</div>
                      <div className="text-white/80 text-sm">{item.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom stats bar */}
            <div className="pt-8 border-t border-white/20">
              <div className="flex flex-wrap justify-center gap-8 md:gap-12">
                {[
                  { icon: Zap, value: '75%', label: 'Faster Operations' },
                  { icon: DollarSign, value: '60%', label: 'Cost Reduction' },
                  { icon: Target, value: '95%', label: 'AI Accuracy' },
                  { icon: Clock, value: '<30 Days', label: 'Average Delivery' }
                ].map((stat, index) => {
                  const StatIcon = stat.icon;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <StatIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-xl font-bold text-white">{stat.value}</div>
                        <div className="text-white/70 text-xs">{stat.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Decorative corner elements */}
        <div className="absolute top-0 left-0 w-40 h-40 border-t-2 border-l-2 border-white/20 rounded-tl-3xl"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 border-b-2 border-r-2 border-white/20 rounded-br-3xl"></div>
      </section>
    </>
  );
};

export default HomePage;